import React from 'react'
import { useFormikContext } from 'formik'
import {
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  TextField,
} from '@mui/material'
import MapIcon from '@mui/icons-material/Map'
import NatureIcon from '@mui/icons-material/Nature'
import StraightenIcon from '@mui/icons-material/Straighten'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import WarningIcon from '@mui/icons-material/Warning'
import PersonIcon from '@mui/icons-material/Person'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SectionSummaryCard from '../components/SectionSummaryCard'
import PhotoUploader from '../components/PhotoUploader'
import RegistrationStatusPanel from '../components/RegistrationStatusPanel'
import ItemsContainer from '../../ItemsContainer'

const pickItems = (items, names) =>
  names.map(name => items.find(i => i.name === name)).filter(Boolean)

const hasValue = value => value !== '' && value !== null && value !== undefined

const SectionHeader = ({ icon: Icon, label }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle2" color="primary" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

const SummaryStep = ({
  itemsGeneral,
  itemsInconvenience,
  groupFieldAutocomplete,
  onEditStep,
  photos,
  onPhotosChange,
  primaryPhotoIndex,
  onPrimaryPhotoChange,
  onDeleteExisting,
  mode = 'create',
  maxPhotos = 6,
  minPhotos = 5,
  zoneTypesData,
  allSiteTypes,
  siteTypesData,
}) => {
  const { values } = useFormikContext()

  const recorderItems = pickItems(itemsGeneral || [], ['recorded_'])

  const riskItemsMap = React.useMemo(() => {
    const map = {}
    if (!itemsInconvenience) return map
    itemsInconvenience
      .filter(item => item.category === 7)
      .forEach(item => {
        map[item.name] = item
      })
    return map
  }, [itemsInconvenience])

  const getRiskLabel = React.useCallback(
    (fieldName, value) => {
      if (value === undefined || value === null || value === '') return ''
      const item = riskItemsMap[fieldName]
      const options = item?.options
      if (!options || typeof options !== 'object') return value
      return options[value] || options[String(value)] || value
    },
    [riskItemsMap],
  )

  // Resolve IDs to display names
  const zoneTypeName = zoneTypesData?.[values.typeSite_]?.nombre || ''
  const siteTypeName = allSiteTypes?.[values.sitePlace_] || ''
  const isPlusCode =
    values.modoCodigoPlusCode !== false && values.modoCodigoPlusCode !== 0

  // Build code preview for structured mode
  let codePreview = ''
  if (isPlusCode) {
    codePreview = 'Plus Code (automático)'
  } else {
    const zPrefix = zoneTypesData?.[values.typeSite_]?.prefijo || '??'
    const sPrefix = siteTypesData?.[values.sitePlace_]?.prefijo || '??'
    const acronym =
      groupFieldAutocomplete?.siteAcronym?.selected?.label ||
      groupFieldAutocomplete?.siteAcronym?.selected ||
      ''
    const section =
      groupFieldAutocomplete?.sectionField?.selected?.label || values.section_ || 'S1'
    codePreview = values.codigoManual || `${zPrefix}.${sPrefix}.${acronym}.${section}.?`
  }

  const locationItems = [
    {
      label: 'Coordenadas',
      value: values.latY && values.longX ? `${values.latY}, ${values.longX}` : '',
    },
    { label: 'Eje verde', value: zoneTypeName },
    { label: 'Emplazamiento', value: siteTypeName },
    {
      label: 'Sitio',
      value: groupFieldAutocomplete?.siteField?.selected?.label || values.site_,
    },
    ...(!isPlusCode
      ? [
          {
            label: 'Sección',
            value:
              groupFieldAutocomplete?.sectionField?.selected?.label || values.section_,
          },
        ]
      : []),
    { label: 'Código', value: codePreview },
    { label: 'Altitud', value: values.altitud ? `${values.altitud} msnm` : '' },
    { label: 'Referencia de ubicación', value: values.referenciaUbicacion_ || '' },
  ]

  const treeItems = [
    {
      label: 'Especie',
      value: groupFieldAutocomplete?.specieField?.selected?.label || '',
    },
    {
      label: 'Nombre común',
      value: Array.isArray(groupFieldAutocomplete?.pseudonymField?.selected)
        ? groupFieldAutocomplete.pseudonymField.selected
            .map(p => p.name || p.label || p)
            .join(', ')
        : '',
    },
    {
      label: 'Estado',
      value: values.alive_ === 1 || values.alive_ === '1' ? 'Vivo' : 'Muerto',
    },
  ]

  const dasometricItems = [
    { label: 'DAP', value: values.dap ? `${values.dap} cm` : '' },
    { label: 'CAP', value: values.cap ? `${values.cap} cm` : '' },
    { label: 'Altura Total', value: values.ht ? `${values.ht} m` : '' },
    { label: 'Altura Comercial', value: values.hc ? `${values.hc} m` : '' },
    { label: 'Copa N-S', value: values.copaNS ? `${values.copaNS} m` : '' },
    { label: 'Copa E-W', value: values.copaEW ? `${values.copaEW} m` : '' },
    {
      label: 'Bifurcaciones',
      value: hasValue(values.bifurcaciones) ? `${values.bifurcaciones} nro` : '',
    },
    {
      label: 'Inclinación',
      value: hasValue(values.inclArbol) ? `${values.inclArbol}°` : '',
    },
    {
      label: 'Compensación',
      value: hasValue(values.compInclArbol) ? `${values.compInclArbol} m` : '',
    },
    { label: 'Rectitud', value: values.fusteRectitud },
    { label: 'Espaciamiento', value: values.espaciamiento },
  ]

  const phytosanitaryItems = [
    { label: 'Follaje', value: values.follaje ? `${values.follaje}%` : '' },
    { label: 'Madurez', value: values.madurez },
    { label: 'Floración', value: values.floracion },
    { label: 'Fructificación', value: values.fructificacion },
  ]

  const riskItems = [
    { label: 'Riesgo de caída', value: getRiskLabel('rsgoCaida', values.rsgoCaida) },
    {
      label: 'Redes eléctricas',
      value: getRiskLabel('rsgoRedesElectricas', values.rsgoRedesElectricas),
    },
    {
      label: 'Afección construcción',
      value: getRiskLabel('rsgoAfeccionConstr', values.rsgoAfeccionConstr),
    },
    {
      label: 'Interf. circulación',
      value: getRiskLabel('rsgoInterfCirculacion', values.rsgoInterfCirculacion),
    },
    {
      label: 'Infraestructura',
      value: getRiskLabel('rsgoInfraestructura', values.rsgoInfraestructura),
    },
    { label: 'Prioridad', value: values.prioridadAtencion || '' },
    { label: 'Otros', value: values.riesgoOtros || '' },
  ]

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={AssignmentIcon} label="Resumen del Registro" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Revise los datos antes de confirmar. Puede editar cada sección si es
            necesario.
          </Typography>

          <Stack spacing={2}>
            <SectionSummaryCard
              icon={MapIcon}
              title="Ubicación"
              items={locationItems}
              onEdit={() => onEditStep(0)}
            />

            <SectionSummaryCard
              icon={NatureIcon}
              title="Datos del Árbol"
              items={treeItems}
              onEdit={() => onEditStep(1)}
            />

            <SectionSummaryCard
              icon={StraightenIcon}
              title="Datos Dasométricos"
              items={dasometricItems}
              onEdit={() => onEditStep(2)}
            />

            <SectionSummaryCard
              icon={HealthAndSafetyIcon}
              title="Estado Fitosanitario"
              items={phytosanitaryItems}
              onEdit={() => onEditStep(3)}
            />

            <SectionSummaryCard
              icon={WarningIcon}
              title="Evaluación de Riesgos"
              items={riskItems}
              onEdit={() => onEditStep(4)}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Photos */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <PhotoUploader
            photos={photos}
            onChange={onPhotosChange}
            primaryIndex={primaryPhotoIndex ?? 0}
            onPrimaryChange={onPrimaryPhotoChange}
            maxPhotos={maxPhotos}
            minPhotos={minPhotos}
            onDeleteExisting={onDeleteExisting}
          />
        </CardContent>
      </Card>

      {/* Collector */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={PersonIcon} label="Responsable" />
          <Grid container spacing={2} alignItems="center">
            {recorderItems.map(item => (
              <ItemsContainer key={item.name} {...item} x={12} s={6} m={6} editable />
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Fecha de registro"
                value={new Date().toLocaleDateString('es-EC', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {mode !== 'edit' && <RegistrationStatusPanel container="card" />}
    </Stack>
  )
}

export default SummaryStep
